import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { Save, X, BellRing, ReceiptText, Globe, ShieldCheck, Wallet, RefreshCw } from "lucide-react";
import { IconTextButton } from "../../components/ButtonComponents";
import { toast } from "react-toastify";
import { useFirmSettingsStore } from "../../store/firmSettingsStore";
import { ConfirmModal } from "../../components/ConfirmModal";
import { PoolCalculationResult } from "../../components/PoolCalculationResult";

interface FirmSettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  settings: any;
  firmName: string;
  onSave: (data: any) => Promise<void>;
}

const syncDirectionOptions = [
  { label: "Çift Yönlü", value: "BIDIRECTIONAL" },
  { label: "Sadece Gelen (ERP -> API)", value: "INBOUND" },
  { label: "Sadece Giden (API -> ERP)", value: "OUTBOUND" },
];

export const FirmSettingsDialog: React.FC<FirmSettingsDialogProps> = ({
  isOpen,
  onClose,
  settings,
  firmName,
  onSave,
}) => {
  const [formData, setFormData] = useState<any>({});
  const [testingFirebase, setTestingFirebase] = useState(false);
  const { testFirebase, triggerPoolCalculation } = useFirmSettingsStore();

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isCalculatingPool, setIsCalculatingPool] = useState(false);
  const [poolCalculationResult, setPoolCalculationResult] = useState<any>(null);

  const [poolTargetMonth, setPoolTargetMonth] = useState<number>(new Date().getMonth() === 0 ? 11 : new Date().getMonth() - 1);
  const [poolTargetYear, setPoolTargetYear] = useState<number>(new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear());

  const monthOptions = [
    { label: "Ocak", value: 0 }, { label: "Şubat", value: 1 }, { label: "Mart", value: 2 },
    { label: "Nisan", value: 3 }, { label: "Mayıs", value: 4 }, { label: "Haziran", value: 5 },
    { label: "Temmuz", value: 6 }, { label: "Ağustos", value: 7 }, { label: "Eylül", value: 8 },
    { label: "Ekim", value: 9 }, { label: "Kasım", value: 10 }, { label: "Aralık", value: 11 }
  ];

  const yearOptions = Array.from({ length: 5 }, (_, i) => {
    const y = new Date().getFullYear() - i;
    return { label: y.toString(), value: y };
  });

  useEffect(() => {
    if (settings && isOpen) {
      setFormData({ ...settings });
      setPoolCalculationResult(null);
    }
  }, [settings, isOpen]);

  const handleTestFirebase = async () => {
    if (!formData.firebaseKey) {
      toast.warn("Firebase JSON anahtarı boş olamaz.");
      return;
    }
    setTestingFirebase(true);
    const result = await testFirebase(formData.firebaseKey);
    if (result.success) {
      toast.success(result.message);
    } else {
      toast.error(result.message);
    }
    setTestingFirebase(false);
  };

  const doTriggerPool = async () => {
    setIsCalculatingPool(true);
    try {
      const res = await triggerPoolCalculation(formData.firmId || formData.id, poolTargetMonth, poolTargetYear);
      if (res.success) {
        toast.success(res.message || "Havuz hesaplaması başarıyla tamamlandı.");
        setPoolCalculationResult(res);
      } else {
        toast.error(res.message || "Havuz hesaplanırken hata oluştu.");
      }
    } catch (error) {
      console.error("Pool calculation error:", error);
      toast.error("Havuz hesaplanırken bir hata oluştu.");
    } finally {
      setIsCalculatingPool(false);
      setIsConfirmOpen(false);
    }
  };

  const handleTriggerPool = () => {
    setIsConfirmOpen(true);
  };

  const handleSubmit = async () => {
    try {
      // Backend'deki create/upsert metodu için gerekli data
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Kaydedilirken bir hata oluştu.");
    }
  };

  return (
    <>
      <ConfirmModal
        isOpen={isConfirmOpen}
        title="Onay"
        message={`${monthOptions.find(m => m.value === poolTargetMonth)?.label} ${poolTargetYear} dönemine ait havuz bakiyelerini yeniden hesaplamak istediğinize emin misiniz?`}
        onConfirm={doTriggerPool}
        onCancel={() => setIsConfirmOpen(false)}
      />
      <Dialog
        header={`${firmName} - Genel Ayarlar`}
        visible={isOpen}
        onHide={onClose}
        className="w-full md:w-10/12 lg:w-8/12"
        modal
        footer={
          <div className="flex justify-end gap-2">
            <IconTextButton onClick={onClose} icon={X} className="btn-cancel">İptal</IconTextButton>
            <IconTextButton onClick={handleSubmit} icon={Save} className="btn-new">Ayarları Kaydet</IconTextButton>
          </div>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-2">

          {/* SOL KOLON: Bölgesel, Yetki ve Otomasyon Ayarları */}
          <div className="space-y-6">

            {/* Bölgesel & Eşitleme */}
            <section>
              <h4 className="flex items-center gap-2 font-bold mb-4 border-b pb-1 opacity-80">
                <Globe size={18} /> Bölgesel & Eşitleme
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-bold uppercase opacity-60">Varsayılan Para Birimi</label>
                  <InputText
                    value={formData.defaultCurrency || ""}
                    onChange={(e) => setFormData({ ...formData, defaultCurrency: e.target.value })}
                    placeholder="TRY"
                    className="p-inputtext-sm"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-bold uppercase opacity-60">Senkronizasyon Yönü</label>
                  <Dropdown
                    value={formData.syncDirection}
                    options={syncDirectionOptions}
                    onChange={(e) => setFormData({ ...formData, syncDirection: e.value })}
                    className="p-inputtext-sm w-full"
                  />
                </div>
              </div>
            </section>

            {/* Yetki & Görünürlük */}
            <section>
              <h4 className="flex items-center gap-2 font-bold mb-4 border-b pb-1 opacity-80">
                <ShieldCheck size={18} /> Yetki & Görünürlük
              </h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 border rounded-lg opacity-90">
                  <div className="flex flex-col">
                    <span className="text-sm font-bold">Plasiyer Tüm Siparişleri Görebilir</span>
                    <small className="text-[10px] opacity-60">Aktif edilirse plasiyerler firmanın tüm sipariş geçmişine erişebilir.</small>
                  </div>
                  <Checkbox
                    inputId="salesSeeAllOrders"
                    checked={formData.salesSeeAllOrders || false}
                    onChange={(e) => setFormData({ ...formData, salesSeeAllOrders: e.checked })}
                  />
                </div>
              </div>
            </section>

            {/* Otomasyon */}
            <section>
              <h4 className="flex items-center gap-2 font-bold mb-4 border-b pb-1 opacity-80">
                <ReceiptText size={18} /> Otomasyon
              </h4>
              <div className="space-y-2">
                {[
                  { id: "autoInvoice", label: "Siparişi Otomatik Faturalandır" },
                  { id: "autoSync", label: "Otomatik Veri Aktarımı (Live Sync)" },
                  { id: "autoCalculateDiscountPool", label: "Havuzları Her Ay Otomatik Hesapla" },
                ].map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-2 rounded-md border border-transparent hover:border-inherit transition-all">
                    <label htmlFor={item.id} className="text-xs font-semibold">{item.label}</label>
                    <Checkbox
                      inputId={item.id}
                      checked={formData[item.id] || false}
                      onChange={(e) => setFormData({ ...formData, [item.id]: e.checked })}
                    />
                  </div>
                ))}
              </div>
            </section>

            {/* Plasiyer İskonto Havuzu */}
            <section>
              <h4 className="flex items-center gap-2 font-bold mb-4 border-b pb-1 opacity-80 text-primary">
                <Wallet size={18} /> Plasiyer İskonto Havuzu
              </h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 border rounded-lg opacity-90">
                  <div className="flex flex-col">
                    <span className="text-sm font-bold">Havuz Sistemi Aktif</span>
                    <small className="text-[10px] opacity-60">Aktif edilirse plasiyerler tahsilatlarından iskonto havuzu bakiyesi kazanır.</small>
                  </div>
                  <Checkbox
                    inputId="enableSalesDiscountPool"
                    checked={formData.enableSalesDiscountPool || false}
                    onChange={(e) => setFormData({ ...formData, enableSalesDiscountPool: e.checked })}
                  />
                </div>

                <div className="flex flex-col md:flex-row items-center justify-between p-3 border rounded-lg bg-bgAlt border-borderColor gap-4">
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-primary">Geçmiş Dönem Hesaplaması</span>
                    <small className="text-[10px] opacity-70">Belirtilen ayın tahsilatlarına göre havuz bakiyelerini oluştur.</small>
                  </div>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-3 w-full">
                    <div className="flex-1 flex gap-2 w-full">
                      <Dropdown
                        value={poolTargetMonth}
                        options={monthOptions}
                        onChange={(e) => setPoolTargetMonth(e.value)}
                        className="p-dropdown-sm flex-1"
                      />
                      <Dropdown
                        value={poolTargetYear}
                        options={yearOptions}
                        onChange={(e) => setPoolTargetYear(e.value)}
                        className="p-dropdown-sm flex-1"
                      />
                    </div>
                    <IconTextButton
                      onClick={handleTriggerPool}
                      icon={RefreshCw}
                      loading={isCalculatingPool}
                      className="bg-danger text-white hover:bg-red-700 whitespace-nowrap w-full sm:w-auto flex justify-center py-1.5"
                    >
                      {isCalculatingPool ? "Hesaplanıyor..." : "Yeniden Hesapla"}
                    </IconTextButton>
                  </div>
                </div>

                {/* Havuz Hesaplama Sonucu */}
                {poolCalculationResult && (
                  <PoolCalculationResult
                    result={poolCalculationResult}
                    periodMonth={poolTargetMonth}
                    periodYear={poolTargetYear}
                    firmId={formData.firmId || formData.id}
                    onClose={() => setPoolCalculationResult(null)}
                  />
                )}

                {formData.enableSalesDiscountPool && (
                  <div className="grid grid-cols-2 gap-4 p-3 bg-bgAlt rounded-lg border border-borderColor">
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] font-bold uppercase opacity-60">Havuz Kazanım Oranı (%)</label>
                      <InputText
                        type="number"
                        step="0.1"
                        value={formData.discountPoolEarnRate || ""}
                        onChange={(e) => setFormData({ ...formData, discountPoolEarnRate: parseFloat(e.target.value) || 0 })}
                        placeholder="Örn: 1.0"
                        className="p-inputtext-sm w-full"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] font-bold uppercase opacity-60">Maksimum İskonto Kullanımı (%)</label>
                      <InputText
                        type="number"
                        step="0.1"
                        value={formData.maxItemPoolDiscountRate || ""}
                        onChange={(e) => setFormData({ ...formData, maxItemPoolDiscountRate: parseFloat(e.target.value) || 0 })}
                        placeholder="Örn: 5.0"
                        className="p-inputtext-sm w-full"
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] font-bold uppercase opacity-60">Bayi Tahsilat Kazanım Oranı (%)</label>
                      <InputNumber
                        value={formData.poolEarnRateDealer}
                        onValueChange={(e) => setFormData({ ...formData, poolEarnRateDealer: e.value ?? 0 })}
                        minFractionDigits={1} maxFractionDigits={2}
                        className="p-inputtext-sm w-full"
                        placeholder="Örn: 2.0"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] font-bold uppercase opacity-60">Standart Tahsilat Kazanım Oranı (%)</label>
                      <InputNumber
                        value={formData.poolEarnRateNormal}
                        onValueChange={(e) => setFormData({ ...formData, poolEarnRateNormal: e.value ?? 0 })}
                        minFractionDigits={1} maxFractionDigits={2}
                        className="p-inputtext-sm w-full"
                        placeholder="Örn: 1.0"
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] font-bold uppercase opacity-60">Normal Cari + Sabit — Toplam Maks. (%)</label>
                      <InputNumber
                        value={formData.maxDiscountNormalWithFixed}
                        onValueChange={(e) => setFormData({ ...formData, maxDiscountNormalWithFixed: e.value ?? 0 })}
                        minFractionDigits={1} maxFractionDigits={2}
                        className="p-inputtext-sm w-full"
                        placeholder="Örn: 5.0"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] font-bold uppercase opacity-60">Normal Cari (Sabit Yok) — Maks. (%)</label>
                      <InputNumber
                        value={formData.maxDiscountNormalNoFixed}
                        onValueChange={(e) => setFormData({ ...formData, maxDiscountNormalNoFixed: e.value ?? 0 })}
                        minFractionDigits={1} maxFractionDigits={2}
                        className="p-inputtext-sm w-full"
                        placeholder="Örn: 10.0"
                      />
                    </div>

                    <div className="flex flex-col gap-2 col-span-2">
                      <label className="text-[10px] font-bold uppercase opacity-60">Bayi Maks. Ek Havuz İskontosu (%)</label>
                      <InputNumber
                        value={(formData as any).maxDiscountDealer ?? 7}
                        onValueChange={(e) => setFormData({ ...formData, maxDiscountDealer: e.value ?? 7 } as any)}
                        minFractionDigits={1} maxFractionDigits={2}
                        className="p-inputtext-sm w-full"
                        placeholder="Örn: 7.0"
                      />
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>

          {/* SAĞ KOLON: Mobil Bildirim (Firebase) */}
          <div className="space-y-6">
            <section className="border rounded-xl p-4 relative">
              <h4 className="flex items-center justify-between font-bold mb-4 opacity-80">
                <div className="flex items-center gap-2">
                  <BellRing size={18} /> Mobil Bildirim (Firebase)
                </div>
                <button
                  type="button"
                  onClick={handleTestFirebase}
                  disabled={testingFirebase}
                  className="text-[10px] font-bold px-2 py-1 rounded border hover:opacity-80 transition-opacity"
                >
                  {testingFirebase ? "TEST EDİLİYOR..." : "BAĞLANTIYI TEST ET"}
                </button>
              </h4>

              <div className="space-y-4">
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-bold uppercase opacity-60">Google Service Account (JSON)</label>
                  <InputTextarea
                    value={formData.firebaseKey || ""}
                    onChange={(e) => setFormData({ ...formData, firebaseKey: e.target.value })}
                    rows={12}
                    className="text-[9px] font-mono leading-tight p-3 rounded-lg shadow-inner w-full"
                    placeholder='{"type": "service_account", ...}'
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-bold uppercase opacity-60">Firebase App ID (Opsiyonel)</label>
                  <InputText
                    value={formData.firebaseAppId || ""}
                    onChange={(e) => setFormData({ ...formData, firebaseAppId: e.target.value })}
                    placeholder="1:123456789:android:abcdef"
                    className="p-inputtext-sm w-full"
                  />
                </div>
              </div>
            </section>
          </div>

        </div>
      </Dialog>
    </>
  );
};